import { drupalPathPrefix } from '#shared/utils/drupal-path-prefix';

const useSiteStore = defineStore('site', {
    state: () => ({ i18nStrategy: 'prefix', locale  : undefined, identifier                : undefined, siteCode                  : undefined, pageIdentifiers           : undefined, defaultLocale             : undefined, gaiaApi                   : undefined, drupalMultisiteIdentifier : undefined, multiSiteCode             : undefined, baseHost                  : undefined, logo                      : undefined, logoDimensions            : undefined, config                    : undefined, name                      : undefined, redirect                  : undefined, drupalInternalRevisionId : undefined, biolandSettings: undefined, env: undefined, }),
    actions:{
        set(name, value){
            this.$patch({ [name]: unref(value) } );

            return this;
        },
        initialize( { biolandSettings, locale, identifier,siteCode, defaultLocale, config, siteName, gaiaApi, multiSiteCode, baseHost, env, homePath }){
            this.set('baseHost',                  baseHost);
            this.set('gaiaApi',                   gaiaApi);
            this.set('drupalMultisiteIdentifier', multiSiteCode);
            this.set('multiSiteCode',             multiSiteCode);
            // Persisted for the `getHost` getter, which hands it to `getCanonicalHost`. BL-946
            // added a second, duplicate write here for the Google tags gate; BL-1015 removed that
            // gate, so this is the only write and `getHost` is the only consumer.
            this.set('env',                       env);
            this.set('locale',                    locale);
            this.set('identifier',                identifier || siteCode);
            this.set('siteCode',                  identifier || siteCode);
            this.set('defaultLocale',             defaultLocale ||config?.defaultLocale);
        
            // A fresh context must not inherit publication or aliases from the previous config.
            this.config = unref(config);
            this.set('logo',   this.getLogoUri);
            this.set('name',   siteName);
            this.set('redirect', (env === 'prod' || env === 'production') ? config?.redirect || '' : '');
            this.set('homePath', homePath);

            // Replace the settings snapshot directly so successive initializations (e.g. locale switch)
            // cannot leave stale nested keys behind via Pinia's recursive $patch merge.
            this.biolandSettings = biolandSettings ? unref(biolandSettings) : undefined;
            
            // Fetch logo dimensions asynchronously
            this.fetchLogoDimensions();
        },
        async fetchLogoDimensions() {
            const logoUrl = this.logo;
            if (!logoUrl) return;
            
            try {
                // Use Image API to get dimensions
                if (import.meta.client) {
                    const img = new Image();
                    await new Promise((resolve, reject) => {
                        img.onload = () => {
                            this.set('logoDimensions', {
                                width: img.naturalWidth,
                                height: img.naturalHeight,
                                type: this.getImageTypeFromUrl(logoUrl)
                            });
                            resolve();
                        };
                        img.onerror = reject;
                        img.src = logoUrl;
                    });
                } else {
                    // For SSR, set default dimensions (will be updated on client)
                    this.set('logoDimensions', {
                        width: 96,
                        height: 96,
                        type: this.getImageTypeFromUrl(logoUrl)
                    });
                }
            } catch (error) {
                // Fallback to default dimensions if image loading fails
                this.set('logoDimensions', {
                    width: 96,
                    height: 96,
                    type: 'image/png'
                });
            }
        },
        getImageTypeFromUrl(url) {
            if (!url) return 'image/png';
            const ext = url.split('.').pop()?.toLowerCase();
            const typeMap = {
                'png': 'image/png',
                'jpg': 'image/jpeg',
                'jpeg': 'image/jpeg',
                'gif': 'image/gif',
                'webp': 'image/webp',
                'svg': 'image/svg+xml'
            };
            return typeMap[ext] || 'image/png';
        },
        getHost(ignoreLocale = false){
            const { locale, siteCode, baseHost, redirect, env } = this;
        
            // Guard against incomplete initialization during SSR
            if (!siteCode || !baseHost) return '';
        
            const pathLocale = ignoreLocale? '' : `/${drupalPathPrefix(locale)}`;
            // The helper owns the redirect decision (env gate plus validation), so ask it
            // first and encode the generated components only when it falls back to them.
            // Leave a redirect Host untouched.
            const canonical = getCanonicalHost({ siteCode, baseHost, env, redirect });
            const base      = canonical === getGeneratedHostname(siteCode, baseHost)
                ? getGeneratedHostname(encodeURIComponent(siteCode), encodeURIComponent(baseHost))
                : canonical;

            return `${base}${pathLocale}`;
        }
    },
    getters:{
        isPromoteAndStickyPublic(){
            const meStore = useMeStore();
            const hasRequiredRole = meStore.roles?.some(role => 
                ['administrator', 'scbd_staff', 'site_manager', 'content_manager', 'contributor'].includes(role)
            );
            
            if (hasRequiredRole) return true;
            
            return this.biolandSettings?.config?.promoteAndStickyPublic || false;
        },
        allLocales(){
            return [...Array.from(new Set([this?.config?.defaultLocale, ...this?.config?.locales|| [] ] || []))];
        },
        // The country flag a Site without its own logo shows, same-origin relative (BL-1071),
        // or '' when the Site has a logo or no country.
        logoFlagUrl(){
            const config     = this.config;
            const hasCountry = config?.country || (config?.countries? config?.countries[0] : undefined);

            return !config?.logo && hasCountry ? getFlagUrl(hasCountry) : '';
        },
        getLogoUri(){
            const config = this.config;

            if(config?.logo)  return config.logo;

            // schema.org/Open Graph consumers of this getter need an absolute URL, so prefix
            // the flag with the host. getHost returns '' during incomplete SSR init (see its own
            // guard) - falling through to the seed default keeps this getter always-absolute
            // instead of silently emitting a relative URL into JSON-LD.
            if(this.logoFlagUrl){
                const host = this.getHost(true);
                if(host) return `${host}${this.logoFlagUrl}`
            }

            return 'https://seed.chm-cbd.net/sites/default/files/images/country/flag/xx.png'
        },
        host(){
            return this.getHost(true);
        },
        localizedHost(){
            return this.getHost();
        },
        params(){
            const { i18nStrategy, identifier, baseHost, siteCode, config, locale, defaultLocale, host, localizedHost, redirect } = this || {};
            const { country:c, countries:cs } = config || {};
            const   countries                 = this.countries || [];
            const   locales                   = this.allLocales;

            return {i18nStrategy,locales, baseHost, siteCode,identifier, country:c, locale, defaultLocale, countries, redirect, host, localizedHost };
        },
        countries(){
            const { config } = this || {};
        
            const countries = config?.countries || config?.runtime?.countries || [];
            const country   = config?.country? [config?.country] : [];
        
            return uniqueArray([  ...country , ...countries ]).filter(falsyFilter);
        },
        // Every theme read goes through resolveTheme — see app/utils/resolve-theme.js for the
        // precedence, per-leaf merge, hero-derive, and totality rules.
        //
        // The Drupal-authored theme is handed in from `this.biolandSettings`, the allowlisted
        // (BL-890) and depth-7 camelCased copy built in server/utils/context-unified.ts and passed
        // through `initialize`. It is deliberately NOT read off `this.config.runTime.biolandSettings`:
        // that copy is the raw dmsm passthrough - neither filtered nor camelCased - so its
        // snake_case keys would not match, and nothing has vetted what is in it.
        theme(){
            return resolveTheme(this.config, this.biolandSettings?.theme);
        },
        primaryColor(){
            return this.theme.color.primary;
        },
        secondaryColor(){
            return this.theme.color.secondary;
        },
        maxLangBeforeWrap(){
            return this.theme.i18n.maxLangBeforeWrap;
        },
        pageInfoColumnRight(){
            return this.theme.page.infoColumn === 'right';
        },
        isBiosafetySite(){
            return this.baseHost.includes('bsl') || this.baseHost.includes('biosafety') || this.baseHost.includes('bch');
        },
        isHomePage(){
            const route = useRoute();
            const path = route?.path;
            
            if (!path) return false;
            
            // Check if path matches root, locale root, or configured homePath
            return path === '/' || 
                   path === `/${this.locale}` || 
                   path === this.homePath;
        }
    }
});

// export { } form: keeps mlly/unimport from reading object keys as extra exports (BL-1178)
export { useSiteStore };
